/**
 * When an in-tab OS notification is due (FAME-858) and what it says. Firing
 * lives in `MessageNotifier`; kept apart from the DOM so both halves test.
 */
import { IS_APP } from "../../vuplex/constants"
import type { Message } from "../types"
import type { Jid } from "../xmpp/jid"

/**
 * Older than this, a message is backlog (mod_offline replays what arrived while
 * we were away as live stanzas, stamped with `<delay>`), not news.
 */
const STALE_AFTER_MS = 5 * 60 * 1000

/** An incoming, recent chat message that still stands; carbons are excluded upstream. */
export function isNotifiable(message: Message, incoming: boolean): boolean {
  return (
    incoming &&
    message.type === "chat" &&
    !message.retracted &&
    Date.now() - message.timestamp < STALE_AFTER_MS
  )
}

let constructorUnsupported = false

/** Android Chrome has permission but no constructor; stop trying once it throws. */
export function setNotificationsUnsupported(unsupported = true) {
  constructorUnsupported = unsupported
}

/**
 * Whether the environment allows one: a browser (not the native wrapper, where
 * OneSignal owns this) with permission granted. Feature-detected — iOS Safari in
 * a plain tab has no API and has the native app. Whether the reader is away is
 * the caller's call.
 */
export function canNotify(): boolean {
  if (IS_APP) return false
  if (constructorUnsupported) return false
  if (!("Notification" in window)) return false
  return Notification.permission === "granted"
}

export type NotificationContent = { title: string; body: string; tag: string }

/**
 * What the notification reads. A group prefixes the sender; the `tag` is the
 * message, so every message gets its own notification.
 */
export function notificationContent(ctx: {
  peer: Jid
  /** The message id — with `peer`, becomes the `tag`. */
  messageId: string
  isGroup: boolean
  /** DM: the peer's display name. Group: the group's name. */
  conversationName: string
  /** The message text, already fallen back to if it was empty. */
  body: string
  /** Group only: prefixes the sender onto the body. */
  formatGroupBody: (body: string) => string
}): NotificationContent {
  return {
    title: ctx.conversationName,
    body: ctx.isGroup ? ctx.formatGroupBody(ctx.body) : ctx.body,
    tag: `${ctx.peer}:${ctx.messageId}`,
  }
}
